// -----------------------------------------------------------------------------
// <copyright file="app.ts" company="ecoInsight, Inc.">
// 	Copyright © ecoInsight, Inc. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------
import {
  Component,
  DOCUMENT,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewContainerRef,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { LoaderModule } from './shared/loader/loader.module';
import { LoaderService } from './shared/loader/loader.service';
import { EDialogService } from './shared/edialog/edialog.service';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { filter, Subject, takeUntil } from 'rxjs';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { GlobalService } from './shared/services/global.service';
import { SharedModule } from './shared/shared.module';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, LoaderModule, SharedModule],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal('VAS');
  private translate = inject(TranslateService);
  private document = inject(DOCUMENT);
  private loader = inject(LoaderService);
  private authService = inject(MsalService);
  public globalService = inject(GlobalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  defaultTheme = signal('default');
  public loading = this.loader.setLoadingState;
  private eDialogService = inject(EDialogService);
  private viewContainerRef = inject(ViewContainerRef);
  private router = inject(Router);
  private readonly _destroying$ = new Subject<void>();
  public displayEnv = signal(true);
  production = environment.production;
  private readonly isIframe = (() => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  })();
  constructor() {
    this.translate.addLangs(['fr', 'en']);
    this.translate.setFallbackLang('en');
    this.translate.use('en');
    this.eDialogService.init(this.viewContainerRef);
    if (this.isIframe) {
      this.displayEnv.set(false);
    }
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this._destroying$),
      )
      .subscribe(() => localStorage.removeItem('reloadCount'));
    // this.loader.setLoadingState.subscribe((data: any) => {
    //   this.loading = data;
    //   this.cdr.detectChanges();
    // });
  }

  public ngOnInit() {
    this.loader.setLoadingState.set(true);
    this.document.body.classList.add(this.defaultTheme());

    this.authService.handleRedirectObservable().subscribe();

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACTIVE_ACCOUNT_CHANGED),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        } else {
          // this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        // this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  checkAndSetActiveAccount() {
    const activeAccount = this.authService.instance.getActiveAccount();
    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }
}
